import { compact } from 'lodash';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { Percent } from '@/graphql/scalars';
import {
  AugmentedCreateHypotheticalCltInput,
  AugmentedCreateHypotheticalCrtInput,
  AugmentedCreateHypotheticalTransferAssetInput,
  AugmentedUpdateHypotheticalCltInput,
  AugmentedUpdateHypotheticalCrtInput,
  CreateHypotheticalTransferFundingSourceInput,
  HypotheticalCltSourceKind,
  HypotheticalCrtSourceKind,
  HypotheticalTransferAssetKind,
  HypotheticalTransferFundingSourceKind,
  TransferMethod,
  UpdateHypotheticalTransferFundingSourceInput,
} from '@/types/schema';
import { assertUnreachable } from '@/utils/errors';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DollarPercentValues } from '../EstateWaterfallHypotheticalTransfers/DollarPercentButtonGroup';
import { CommonHypotheticalTransferFormShape } from '../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.types';
import {
  HypotheticalCharitableTrustFormShape,
  HypotheticalCharitableTrustModalKind,
} from './EstateWaterfallHypotheticalCharitableTrustModal.types';
import { EstateWaterfallHypotheticalCharitableTrustModalQuery } from './graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';

export function getModalHeading({
  isCreating,
  trustKindLabel: trustKind,
}: {
  isCreating: boolean;
  trustKindLabel: string;
}) {
  if (isCreating) {
    return `Create hypothetical ${trustKind}`;
  }

  return `Edit hypothetical ${trustKind}`;
}

export function getButtonText({ isCreating }: { isCreating: boolean }) {
  if (isCreating) {
    return 'Create transfer';
  }

  return 'Save changes';
}

interface MapQueryDataToFormDataInput {
  data: EstateWaterfallHypotheticalCharitableTrustModalQuery;
  hypotheticalWaterfallId: string;
  initialSourceId?: string;
  hypotheticalCharitableTrustId?: string;
  displayName?: string;
  sourceId?: string;
  simulatedProposalId?: string;
}

export const mapQueryDataToFormData = ({
  data,
  hypotheticalWaterfallId,
  initialSourceId,
  hypotheticalCharitableTrustId,
  displayName,
  sourceId: externalSourceId,
  simulatedProposalId: externalSimulatedProposalId,
}: MapQueryDataToFormDataInput):
  | HypotheticalCharitableTrustFormShape
  | undefined => {
  const waterfall = getNodes(data.estateWaterfalls)[0];
  const existingCrt = getNodes(waterfall?.hypotheticalCrts).find(
    (hc) => hc.id === hypotheticalCharitableTrustId
  );
  const existingClt = getNodes(waterfall?.hypotheticalClts).find(
    (hc) => hc.id === hypotheticalCharitableTrustId
  );

  if (!existingCrt && !existingClt) {
    const defaultValues = getDefaultValues({
      sourceId: initialSourceId || externalSourceId,
      displayName,
      simulatedProposalId: externalSimulatedProposalId,
    });
    return defaultValues;
  }

  const existingHypothetical = existingCrt || existingClt!;

  // Prefer to use the value from the form, but fall back to the value from the query
  const simulatedProposalId =
    externalSimulatedProposalId ||
    ('simulatedCrt' in existingHypothetical
      ? existingHypothetical.simulatedCrt.id
      : existingHypothetical.simulatedClt.id);

  // Determine the source ID based on sourceKind
  const sourceId = (() => {
    switch (existingHypothetical.sourceKind) {
      case HypotheticalCrtSourceKind.Entity:
      case HypotheticalCltSourceKind.Entity:
        return existingHypothetical.sourceEntity?.id ?? '';
      case HypotheticalCrtSourceKind.Individual:
      case HypotheticalCltSourceKind.Individual:
        return existingHypothetical.sourceIndividual?.id ?? '';
      default:
        return '';
    }
  })();

  return {
    displayName: existingHypothetical.displayName,
    hypotheticalWaterfallId: hypotheticalWaterfallId,
    simulatedProposalId,
    sourceId,
    fundingKind: getFormFundingKindFromAPI(existingHypothetical.funding?.kind),
    applyEntityTaxDiscount: false,
    entityTaxDiscountRate: null,
    proRataPattern: existingHypothetical.funding?.proRataAmount
      ? DollarPercentValues.DOLLAR
      : DollarPercentValues.PERCENT,
    proRataAmount: existingHypothetical.funding?.proRataAmount ?? null,
    proRataPercent: existingHypothetical.funding?.proRataPercentage ?? null,
    assetsPattern:
      existingHypothetical.funding?.kind ===
      HypotheticalTransferFundingSourceKind.SpecificAssetsPercentage
        ? 'percent'
        : 'amount',
    selectedAssetClassesAndBusinessIds: compact(
      getNodes(existingHypothetical.funding?.assets).map(
        (asset) => asset.assetClass?.id || asset.ownedBusiness?.id
      )
    ),
    selectedAssetClassAndBusinessValues: getNodes(
      existingHypothetical.funding?.assets
    ).reduce(
      (acc, asset) => {
        if (asset.assetClass?.id) {
          acc[asset.assetClass?.id] = {
            amount: asset.valueAmount ?? null,
            percent: asset.valuePercentage ?? null,
          };
        }

        if (asset.ownedBusiness?.id) {
          acc[asset.ownedBusiness?.id] = {
            amount: asset.valueAmount ?? null,
            percent: asset.valuePercentage ?? null,
          };
        }

        return acc;
      },
      {} as CommonHypotheticalTransferFormShape['selectedAssetClassAndBusinessValues']
    ),
    forceLiquidationOnTransfer:
      existingHypothetical.funding?.transferMethod ===
      TransferMethod.LiquidateToCash,
  };
};

export function getSourceIndividualOptions(
  data: EstateWaterfallHypotheticalCharitableTrustModalQuery
): TypeaheadSelectInputOption<string>[] {
  const household = getNodes(data.households)[0];
  const possibleGrantors = household?.sourceIndividualOptions ?? [];

  return possibleGrantors.map((individual) => ({
    value: individual.id,
    display: individual.displayName,
    groupName: 'Individuals',
  }));
}

export function mapSourceEntityOptions(
  data: EstateWaterfallHypotheticalCharitableTrustModalQuery
): TypeaheadSelectInputOption<string>[] {
  return getNodes(data.sourceEntityOptions).map((entity) => {
    return {
      value: entity.id,
      display: entity.subtype?.displayName ?? '',
      groupName: 'Entities',
    };
  });
}

export const mapFormDataToCreateInput = (
  formData: HypotheticalCharitableTrustFormShape,
  waterfallId: string,
  kind: HypotheticalCharitableTrustModalKind,
  householdId: string
):
  | AugmentedCreateHypotheticalCrtInput
  | AugmentedCreateHypotheticalCltInput => {
  const fundingKind = getFundingKind(formData);
  const isEntity = getPulidKind(formData.sourceId) === PulidKind.Entity;

  const commonFundingInput: CreateHypotheticalTransferFundingSourceInput = {
    kind: fundingKind,
    householdID: householdId,
    proRataAmount:
      fundingKind === HypotheticalTransferFundingSourceKind.ProRataAmount
        ? formData.proRataAmount
        : null,
    proRataPercentage:
      fundingKind === HypotheticalTransferFundingSourceKind.ProRataPercentage
        ? formData.proRataPercent
        : null,
    transferMethod:
      formData.forceLiquidationOnTransfer === true
        ? TransferMethod.LiquidateToCash
        : TransferMethod.InKind,
  };

  if (kind === HypotheticalCharitableTrustModalKind.CRT) {
    const crtSourceKind = isEntity
      ? HypotheticalCrtSourceKind.Entity
      : HypotheticalCrtSourceKind.Individual;

    const input: AugmentedCreateHypotheticalCrtInput = {
      create: {
        displayName: formData.displayName,
        hypotheticalWaterfallID: waterfallId,
        simulatedCrtID: formData.simulatedProposalId,
        sourceKind: crtSourceKind,
        sourceIndividualID: isEntity ? undefined : formData.sourceId,
        sourceEntityID: isEntity ? formData.sourceId : undefined,
        householdID: householdId,
      },
      withFunding: {
        create: commonFundingInput,
        withAssets: mapFormDataToAssetInputs(formData, householdId),
      },
    };
    return input;
  } else {
    const cltSourceKind = isEntity
      ? HypotheticalCltSourceKind.Entity
      : HypotheticalCltSourceKind.Individual;

    const input: AugmentedCreateHypotheticalCltInput = {
      create: {
        displayName: formData.displayName,
        hypotheticalWaterfallID: waterfallId,
        simulatedCltID: formData.simulatedProposalId,
        sourceKind: cltSourceKind,
        sourceIndividualID: isEntity ? undefined : formData.sourceId,
        sourceEntityID: isEntity ? formData.sourceId : undefined,
        householdID: householdId,
      },
      withFunding: {
        create: commonFundingInput,
        withAssets: mapFormDataToAssetInputs(formData, householdId),
      },
    };
    return input;
  }
};

export const mapFormDataToUpdateInput = (
  formData: HypotheticalCharitableTrustFormShape,
  hypotheticalCharitableTrustId: string,
  hypotheticalCharitableTrustFundingId: string,
  kind: HypotheticalCharitableTrustModalKind,
  householdId: string
):
  | AugmentedUpdateHypotheticalCrtInput
  | AugmentedUpdateHypotheticalCltInput => {
  const fundingKind = getFundingKind(formData);
  const isEntity = getPulidKind(formData.sourceId) === PulidKind.Entity;

  const commonFundingInput: UpdateHypotheticalTransferFundingSourceInput = {
    kind: fundingKind,
    clearAssets: true,
    proRataAmount:
      fundingKind === HypotheticalTransferFundingSourceKind.ProRataAmount
        ? formData.proRataAmount
        : null,
    proRataPercentage:
      fundingKind === HypotheticalTransferFundingSourceKind.ProRataPercentage
        ? formData.proRataPercent
        : null,
    clearProRataAmount:
      fundingKind !== HypotheticalTransferFundingSourceKind.ProRataAmount,
    clearProRataPercentage:
      fundingKind !== HypotheticalTransferFundingSourceKind.ProRataPercentage,
    transferMethod: formData.forceLiquidationOnTransfer
      ? TransferMethod.LiquidateToCash
      : TransferMethod.InKind,
  };

  if (kind === HypotheticalCharitableTrustModalKind.CRT) {
    const crtSourceKind = isEntity
      ? HypotheticalCrtSourceKind.Entity
      : HypotheticalCrtSourceKind.Individual;

    const input: AugmentedUpdateHypotheticalCrtInput = {
      id: hypotheticalCharitableTrustId,
      update: {
        displayName: formData.displayName,
        simulatedCrtID: formData.simulatedProposalId,
        sourceKind: crtSourceKind,
        sourceIndividualID: isEntity ? undefined : formData.sourceId,
        sourceEntityID: isEntity ? formData.sourceId : undefined,
      },
      updateFunding: {
        id: hypotheticalCharitableTrustFundingId,
        update: commonFundingInput,
        withAssets: mapFormDataToAssetInputs(formData, householdId),
      },
    };
    return input;
  } else {
    const cltSourceKind = isEntity
      ? HypotheticalCltSourceKind.Entity
      : HypotheticalCltSourceKind.Individual;

    const input: AugmentedUpdateHypotheticalCltInput = {
      id: hypotheticalCharitableTrustId,
      update: {
        displayName: formData.displayName,
        simulatedCltID: formData.simulatedProposalId,
        sourceKind: cltSourceKind,
        sourceIndividualID: isEntity ? undefined : formData.sourceId,
        sourceEntityID: isEntity ? formData.sourceId : undefined,
      },
      updateFunding: {
        id: hypotheticalCharitableTrustFundingId,
        update: commonFundingInput,
        withAssets: mapFormDataToAssetInputs(formData, householdId),
      },
    };
    return input;
  }
};

interface GetDefaultValuesInput {
  sourceId?: string;
  displayName?: string;
  simulatedProposalId?: string;
}

export function getDefaultValues(
  input?: GetDefaultValuesInput
): HypotheticalCharitableTrustFormShape {
  return {
    displayName: input?.displayName ?? '',
    hypotheticalWaterfallId: '',
    simulatedProposalId: input?.simulatedProposalId ?? '',
    sourceId: input?.sourceId ?? '',
    fundingKind: 'proRata',
    proRataPattern: DollarPercentValues.DOLLAR,
    proRataAmount: null,
    proRataPercent: null,
    assetsPattern: 'amount',
    selectedAssetClassesAndBusinessIds: [],
    selectedAssetClassAndBusinessValues: {},
    applyEntityTaxDiscount: false,
    entityTaxDiscountRate: null,
    forceLiquidationOnTransfer: false,
  };
}

export function getProposalOptions(
  data: EstateWaterfallHypotheticalCharitableTrustModalQuery,
  kind: HypotheticalCharitableTrustModalKind,
  hypotheticalCharitableTrustId?: string
): TypeaheadSelectInputOption<string>[] {
  const linkedCrts = getNodes(
    getNodes(data.estateWaterfalls)[0]?.hypotheticalCrts
  );
  const linkedClts = getNodes(
    getNodes(data.estateWaterfalls)[0]?.hypotheticalClts
  );

  const linkedProposals = [
    ...(kind === HypotheticalCharitableTrustModalKind.CRT ? linkedCrts : []),
    ...(kind === HypotheticalCharitableTrustModalKind.CLT ? linkedClts : []),
  ]
    .filter((hc) => hc.id !== hypotheticalCharitableTrustId)
    .flatMap((hc) => {
      if ('simulatedCrt' in hc) {
        return hc.simulatedCrt.id;
      } else if ('simulatedClt' in hc) {
        return hc.simulatedClt.id;
      }

      return [];
    });

  function getProposalOption(proposal: {
    id: string;
    displayName: string;
    crtProposal?: {
      id: string;
      termYears: number;
      rate7520?: Percent | null;
    } | null;
    cltProposal?: {
      id: string;
      termYears: number;
      rate7520?: Percent | null;
    } | null;
  }) {
    const isLinked = linkedProposals.includes(proposal.id);
    const proposalData =
      kind === HypotheticalCharitableTrustModalKind.CRT
        ? proposal.crtProposal
        : proposal.cltProposal;
    const termYears = proposalData?.termYears ?? null;
    const rate7520 = proposalData?.rate7520 ?? null;
    const isDisabled = isLinked || !termYears || !rate7520;

    const endLabel = (() => {
      if (isLinked) {
        return 'This proposal is already modeled';
      }

      if (!termYears) {
        return 'This proposal does not have a term duration';
      }

      if (!rate7520) {
        return 'This proposal does not have a 7520 rate';
      }

      return undefined;
    })();

    return {
      value: proposal.id,
      display: proposal.displayName,
      details: termYears
        ? `${proposal.displayName} - ${termYears} Year Term`
        : '',
      disabled: isDisabled,
      endLabel,
    };
  }

  switch (kind) {
    case HypotheticalCharitableTrustModalKind.CRT:
      return getNodes(data.crtOptions).map(getProposalOption);
    case HypotheticalCharitableTrustModalKind.CLT:
      return getNodes(data.cltOptions).map(getProposalOption);
    default:
      return [];
  }
}

const mapFormDataToAssetInputs = (
  formData: HypotheticalCharitableTrustFormShape,
  householdId: string
): AugmentedCreateHypotheticalTransferAssetInput[] => {
  if (formData.fundingKind !== 'assets') {
    return [];
  }

  return formData.selectedAssetClassesAndBusinessIds.map((id) => {
    const value = formData.selectedAssetClassAndBusinessValues[id];
    if (!value) {
      throw new Error(`Missing value for asset/business ${id}`);
    }

    const kind =
      getPulidKind(id) === PulidKind.AssetClass
        ? HypotheticalTransferAssetKind.AssetClass
        : HypotheticalTransferAssetKind.BusinessOwnership;

    const createAssetInput: AugmentedCreateHypotheticalTransferAssetInput = {
      create: {
        kind,
        assetClassID:
          kind === HypotheticalTransferAssetKind.AssetClass ? id : undefined,
        ownedBusinessID:
          kind === HypotheticalTransferAssetKind.BusinessOwnership
            ? id
            : undefined,
        valueAmount:
          formData.assetsPattern === 'amount' ? (value.amount ?? null) : null,
        valuePercentage:
          formData.assetsPattern === 'percent' ? (value.percent ?? null) : null,
        householdID: householdId,
      },
    };

    return createAssetInput;
  });
};

function getFundingKind(
  formData: HypotheticalCharitableTrustFormShape
): HypotheticalTransferFundingSourceKind {
  if (formData.fundingKind === 'proRata') {
    if (formData.proRataPattern === 'amount') {
      return HypotheticalTransferFundingSourceKind.ProRataAmount;
    } else if (formData.proRataPattern === 'percent') {
      return HypotheticalTransferFundingSourceKind.ProRataPercentage;
    } else {
      throw new Error(`Unhandled proRata pattern: ${formData.proRataPattern}`);
    }
  }

  if (formData.fundingKind === 'assets') {
    if (formData.assetsPattern === 'amount') {
      return HypotheticalTransferFundingSourceKind.SpecificAssetsAmount;
    } else if (formData.assetsPattern === 'percent') {
      return HypotheticalTransferFundingSourceKind.SpecificAssetsPercentage;
    } else {
      throw new Error(`Unhandled assets pattern: ${formData.assetsPattern}`);
    }
  }

  throw new Error(`Unhandled funding kind: ${formData.fundingKind}`);
}

const getFormFundingKindFromAPI = (
  kind: HypotheticalTransferFundingSourceKind | undefined
): HypotheticalCharitableTrustFormShape['fundingKind'] | null => {
  switch (kind) {
    case HypotheticalTransferFundingSourceKind.ProRataAmount:
      return 'proRata';
    case HypotheticalTransferFundingSourceKind.ProRataPercentage:
      return 'proRata';
    case HypotheticalTransferFundingSourceKind.SpecificAssetsAmount:
      return 'assets';
    case HypotheticalTransferFundingSourceKind.SpecificAssetsPercentage:
      return 'assets';
    default:
      return null;
  }
};

export function getHypotheticalCharitableTrustModalKindLabel(
  kind: HypotheticalCharitableTrustModalKind
) {
  switch (kind) {
    case HypotheticalCharitableTrustModalKind.CRT:
      return 'CRT';
    case HypotheticalCharitableTrustModalKind.CLT:
      return 'CLT';
    default:
      assertUnreachable(kind, {
        message: `Invalid hypothetical charitable trust modal kind: ${kind}`,
      });
  }
}
